import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useVault } from '../session/useVault';
import { useWhenRefused } from '../session/useWhenRefused';
import { pendingText } from '../vault/writeQueue';
import { parseEntity } from '../entity/entity';
import { heldEntity } from '../entity/held';
import type { VaultEntity } from '../entity/entity';
import { buildVaultIndex } from '../entity/vaultIndex';
import { useCatalog } from '../entity/catalogStore';
import { EMPTY_REGISTRIES } from '../plan/registries';
import { usePlan } from '../plan/usePlan';
import { kindOf, listOf } from '../entity/entity';
import { EntityPage, entityHref } from './EntityPage';
import { kindByFolder } from '../vault/kinds';
import { faceRebased, facePicks, withFace } from '../entity/face';
import { reorderList, withVisibility } from '../plan/edits';
import type { Pick } from '../entity/entity';
import { useTheme } from '../design/useTheme';
import { NookLoading, NookPage } from '../nook/NookPage';
import { itemKind } from '../nook/nook';
import { RecordingPage } from '../doc/RecordingPage';
import { NotePage } from '../doc/NotePage';
import { Loading } from '../shell/Loading';

/**
 * Any vault file, read on demand.
 *
 * The plan board can afford to load every arc — there are under a hundred.
 * The vault holds thousands of files, so anything that is not an arc is
 * fetched one at a time, by path, when someone asks for it. That is the
 * difference between a board and a browser, and it is why this route reads
 * a single blob rather than going through `loadPlan`.
 *
 * The page itself is the same one the arc uses. Which blocks appear comes
 * from the file, so a recording renders its topics and none of the plan's
 * vocabulary without this route knowing what a recording is.
 */
export function FileRoute() {
  const location = useLocation();
  const { state, write, commit } = useVault();
  const { plan } = usePlan();
  const { theme } = useTheme();
  const catalog = useCatalog();
  const [entity, setEntity] = useState<VaultEntity | null>(null);
  const [missing, setMissing] = useState(false);

  const path = decodeURIComponent(location.pathname.replace(/^\/file\//, ''));
  const openPath = useRef(path);
  openPath.current = path;

  // The page shows its edits before they are written. A file a newer app wrote refuses them, and
  // the page goes back to what this tab holds of the file.
  useWhenRefused(path, () => {
    if (state.status !== 'ready') return;
    const refused = path;
    void heldEntity(state.vault, refused)
      .then((now) => {
        if (now && openPath.current === refused) setEntity(now);
      })
      .catch(() => {});
  });

  useEffect(() => {
    if (state.status !== 'ready') return;
    let cancelled = false;
    // A re-read of the same file keeps the page up; only a different file
    // starts from nothing.
    setEntity((current) => (current?.path === path ? current : null));
    setMissing(false);
    // A file this tab has just written may not have reached the vault yet.
    const queued = pendingText(path);
    if (queued !== undefined) {
      setEntity(parseEntity(path, queued));
      return;
    }
    state.vault
      .readFile(path)
      .then((text) => {
        if (cancelled) return;
        if (text === null) setMissing(true);
        else setEntity(parseEntity(path, text));
      })
      .catch(() => !cancelled && setMissing(true));
    return () => {
      cancelled = true;
    };
  }, [state, path]);

  // Which arcs cite this file — a derivation over the shared plan rather than
  // a load of its own, so it can never answer from a different read than the
  // page that linked here.
  const inArcs = useMemo(
    () =>
      plan.status === 'ready'
        ? plan.plan.arcs.filter((arc) =>
            listOf(arc.frontMatter['sources']).some((s) => s.split(' ^')[0]!.trim() === path),
          )
        : [],
    [plan, path],
  );

  if (state.status === 'disconnected') {
    return (
      <section className="placeholder">
        <h1>Not connected</h1>
        <p>
          <Link to="/connect">Connect this browser</Link> to read your vault.
        </p>
      </section>
    );
  }

  /** The arcs are already loaded, so a reference to one answers with the real
   *  file rather than a stub — which is what lets a workspace's holds panel
   *  show its members' states, and open a storyline it holds. Everything else
   *  stays a stub: reading every referenced file to get its title would defeat
   *  reading one file at a time. */
  const loadedArcs = plan.status === 'ready' ? plan.plan.arcs : [];

  // The arc route is where an arc is worked — tints, status, its storyline —
  // and it finds one by name, so a name two arcs share stays here.
  const asArc = loadedArcs.find((a) => a.path === path);
  if (asArc && loadedArcs.find((a) => a.name === asArc.name) === asArc) {
    return <Navigate replace to={entityHref(asArc.name)} />;
  }

  if (missing) {
    return (
      <section className="placeholder">
        <h1>Not in the vault</h1>
        <p className="placeholder-phase">{path}</p>
        <p>
          <Link to="/plan">Back to the board</Link>
        </p>
      </section>
    );
  }

  // A nook is sat in on its plane, not read as a page. Its folder or its
  // catalog row says so before the file does, so the plane draws while the
  // file is still coming.
  if (!entity && (kindByFolder(path) === 'workspace' || catalog.get(path)?.kind === 'workspace')) {
    return <NookLoading />;
  }

  if (!entity) return <Loading title="Opening…" />;

  const index = state.status === 'ready' ? buildVaultIndex(state.vault) : null;
  const resolvePath = (name: string) => index?.pathFor(name) ?? null;

  /** Applied locally as well as queued: the write is debounced by seconds, and
   *  a heart that waits for the vault to answer reads as a control that did
   *  not work. */
  const setFace = (picks: Pick[]) => {
    const before = facePicks(path, entity.raw);
    const change = (t: string) => withFace(t, faceRebased(path, t, before, picks));
    const next = change(entity.raw);
    setEntity(parseEntity(path, next));
    write([{ path, text: next, change }]);
  };

  /**
   * Reordering the arcs this file holds — a workspace's plan, most often.
   *
   * The write goes to whichever file OWNS the list: this one for its own
   * `arcs:`, and a storyline's own file when one is opened in place inside it.
   * Applied locally for this file the way [setFace] is, since the queued write
   * is debounced and a row that springs back reads as a drag that failed.
   */
  const reorderHeld = (target: VaultEntity, order: string[]) => {
    const change = (t: string) => reorderList(t, 'arcs', order);
    const text = change(target.raw);
    if (text === target.raw) return;
    if (target.path === path) setEntity(parseEntity(path, text));
    write([{ path: target.path, text, change }]);
  };

  /** Files a recording away, or brings it back: its `visibility:` line, which the app reads back
   *  as the recording's own state. Applied here as well as queued, so the menu follows. */
  const setArchived = (archived: boolean) => {
    const change = (t: string) => withVisibility(t, archived ? 'archived' : 'visible');
    const text = change(entity.raw);
    if (text === entity.raw) return;
    setEntity(parseEntity(path, text));
    void commit([{ path, text, change }]);
  };

  // A file is named at its path when it is made and never renamed after, so
  // one renamed in the app — a workspace, a recording — still sits at its
  // original filename and only its catalog row says what it is called now.
  const named = catalog.get(path)?.name ?? null;

  if (kindOf(entity) === 'workspace') {
    return <NookPage entity={named ? { ...entity, title: named } : entity} />;
  }

  // A recording and a note read as documents; anything else keeps the page
  // that shows a file whatever it holds.
  const kind = itemKind(path, entity.raw);
  if (kind === 'recording') return <RecordingPage entity={entity} onSetFace={setFace} onSetArchived={setArchived} />;
  if (kind === 'note') return <NotePage entity={entity} />;

  return (
    <EntityPage
      entity={named ? { ...entity, title: named } : entity}
      registries={EMPTY_REGISTRIES}
      theme={theme}
      onSetFace={setFace}
      inArcs={inArcs}
      onReorderHeld={reorderHeld}
      kindAt={(target) => catalog.get(target)?.kind ?? null}
      resolveName={(name) => {
        const loaded = loadedArcs.find((a) => a.name === name.replace(/^.*\//, ''));
        if (loaded) return loaded;
        const target = resolvePath(name);
        // Enough to render a link: the page only needs a name and a title
        // to point at something, and reading every referenced file to get
        // its real title would defeat reading one file at a time.
        return target ? stub(target, catalog.get(target)?.name ?? null) : null;
      }}
      titleOf={() => null}
    />
  );
}

/** A reference we can link to without having opened it. [named] is what the
 *  catalog calls it, which is the only current record of a rename. */
function stub(path: string, named: string | null): VaultEntity {
  const name = path.replace(/^.*\//, '');
  return { path, name, raw: '', title: named ?? name.replace(/\.md$/, ''), frontMatter: {}, body: '' };
}
